import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useDispatch, useSelector } from 'react-redux';
import Dropdown from '../../../../components/dropdown/dropdown';
import Input from '../../../../components/input/input';
import InfoCard from '../../../../components/info-card/infoCard';
import '../style';
import { isEmpty } from 'lodash';
import mixpanel from 'mixpanel-browser';

const Step_3 = ({ categoryPrice, error, setError }) => {
  
  const dispatch = useDispatch();
  const { userData } = useSelector((state) => state.app);
  const { eventData, ticketData, verificationData } = useSelector((state) => state.sellTickets);

  useEffect(() => {
    window.scrollTo(0, 0);

    mixpanel.track("Price_Your_Tickets_Page_Loaded", {
      "event_id": eventData?._id || null,
      "event_name": eventData?.name || null,
      "category_name": verificationData?.eventCategory || null,
      "user_id": userData?._id,
      "page_url": window.location.href,
    });
  }, []);

  const setTicketPrice = (price) => {
    dispatch({ type: 'SELL_TICKET:TICKET_DATA:SET', data: { ...ticketData, price } });
  };

  const setTicketCount = (count) => {
    dispatch({ type: 'SELL_TICKET:TICKET_DATA:SET', data: { ...ticketData, count } });
  };

  // Validation functions
  const validateTicketCount = (count) => {
    if (count <= 0) {
      setError({ ...error, count: 'Ticket count cannot be zero or empty.' });
    } else if (count > 10) {
      setError({ ...error, count: 'Ticket count cannot be higher than 10.' });
    } else {
      setError({ ...error, count: '' });
    }
  };

  const validateTicketPrice = (price) => {
    if (!price || price <= 0) {
      setError({ ...error, price: 'Ticket price cannot be zero or empty.' });
    } else if (price > ((verificationData?.totalPrice / verificationData?.quantity) || categoryPrice || 1000)) {
      setError({ ...error, price: 'Ticket price cannot be higher than the original price.' });
    } else {
      setError({ ...error, price: '' });
    }
  };

  useEffect(() => {
    if (!isEmpty(ticketData?.count)) validateTicketCount(ticketData?.count);
    if (!isEmpty(ticketData?.price)) validateTicketPrice(ticketData?.price);
  }, [ticketData]);

  const handleTicketCountChange = (value) => {
    const count = Number(value);
    setTicketCount(count);
    validateTicketCount(count);
  };

  const handleTicketPriceChange = (e) => {
    const value = e.target.value;
    setTicketPrice(value);
  };

  const handleSuggestedPriceClick = (price) => {
    setTicketPrice(price);
    setError('');
  };

  const mrp = (verificationData?.totalPrice / verificationData?.quantity) || categoryPrice || 1000; 
  const options = Array(verificationData?.quantity).fill(1).map((n, idx) => ({
    id: (n + idx).toString(),
    label: (n + idx).toString(),
  }));

  const roundToNearest = (number, multiples) => {
    const roundedValues = multiples.map(
      (multiple) => Math.round(number / multiple) * multiple,
    );
    const differences = roundedValues.map((value) => Math.abs(number - value));
    const minDifference = Math.min(...differences);
    const index = differences.indexOf(minDifference);
    return roundedValues[index];
  };

  const firstSuggestedPrice = roundToNearest(Math.floor(mrp - 100), [50, 100]) < 0
    ? 0
    : roundToNearest(Math.floor(mrp - 100), [50, 100]);
  const secondSuggestedPrice = roundToNearest(Math.floor(mrp - 50), [50, 100]) < 0
    ? 0
    : roundToNearest(Math.floor(mrp - 50), [50, 100]);
  const thirdSuggestedPrice = Math.floor(mrp) < 0
    ? 0
    : Math.floor(mrp);

  return (
    <div className="flex flex-col justify-between">
      <Helmet>
        <title>Sell tickets</title>
        <meta
          name="Sell Tickets Easily in India | Reach Your Audience on Take My Tickets"
          content="Start selling your event tickets hassle-free on Take My Tickets, India's trusted ticket resale platform. Add seller contact details effortlessly."
        />
        <meta charset="UTF-8" />
      </Helmet>
      <div className="flex flex-col gap-6 py-9">
        <div className="md:heading-36 heading-24">
          Price your “{verificationData?.eventCategory} tickets”
        </div>
        <Dropdown
          width="100%"
          label="How many tickets do you want to sell?"
          options={options}
          value={ticketData?.count?.toString()}
          onChange={handleTicketCountChange}
          className="w-full mb-2 items-center ticket-count"
          placeholder="Count"
          validationStatus={error?.count ? 'error' : ''}
          helpText={error?.count}
        />

        <Input
          label="Price per ticket"
          contentLeft="₹"
          placeholder="0"
          className="w-full"
          value={ticketData?.price}
          onChange={handleTicketPriceChange}
          validationStatus={error?.price ? 'error' : ''}
          helpText={error?.price}
          type="number"
        />

        <div className="label-14 text-grey-70 gap-2 flex flex-col">
          Suggested prices:
          <div className="flex justify-between gap-2 md:gap-2 w-full">
            <div
              className={`w-full px-6 py-3 pb-5 md:px-12 md:pt-4 md:pb-5 border rounded-lg border-grey-30 shadow-xs hover:border-black hover:bg-white ${ticketData?.price === firstSuggestedPrice ? 'border-primary bg-[#FFF2F2]' : ''} justify-center flex flex-col relative items-center label-14 cursor-pointer`}
              onClick={() => handleSuggestedPriceClick(firstSuggestedPrice)}
            >
              ₹{firstSuggestedPrice}
              <div className="w-full border border-primary text-white font-semibold text-[11px] rounded-b-md px-0.5 bg-primary items-center absolute bottom-0 text-center">
                Sells fast
              </div>
            </div>
            <div
              className={`w-full px-6 py-3 md:px-12 md:pt-4 md:pb-5 border rounded-lg border-grey-30 shadow-xs hover:border-black hover:bg-white ${ticketData?.price === secondSuggestedPrice ? 'border-primary bg-[#FFF2F2]' : ''} justify-center flex flex-col items-center label-14 cursor-pointer`}
              onClick={() => handleSuggestedPriceClick(secondSuggestedPrice)}
            >
              ₹{secondSuggestedPrice}
            </div>
            <div
              className={`w-full px-6 py-3 md:px-12 md:pt-4 md:pb-5 border rounded-lg border-grey-30 shadow-xs hover:border-black hover:bg-white ${ticketData?.price === thirdSuggestedPrice ? 'border-primary bg-[#FFF2F2]' : ''} justify-center flex flex-col items-center label-14 cursor-pointer`}
              onClick={() => handleSuggestedPriceClick(thirdSuggestedPrice)}
            >
              ₹{thirdSuggestedPrice}
            </div>
          </div>
        </div>

        <InfoCard
          content="Please price your tickets at what you paid, or lower. Selling above what you paid is against the law."
          className="info"
        />
      </div>

      <div className="text-14 flex gap-1 items-center justify-center">
        <>💡</>You can always edit the prices later.
      </div>
    </div>
  );
};

export default Step_3;
